import React, { ReactElement, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconProp } from '@fortawesome/fontawesome-svg-core'

import { SortConfig, TableItemProps, TableProps } from './table.types'
import useSortableData from '../../../application/hook/useSort'
import { filterData } from './table.helpers'
import Checkbox from '../checkbox'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import {
  StyledTd,
  StyledTh,
  StyledTable,
  StyledThead,
  StyledTbody,
  TableWrapper,
  ViewButton,
  DownloadAllButton,
  LoadingDownloadButton,
} from './table.styles'

const TableItem: React.FC<TableItemProps> = (props) => {
  const { item, isSelected, viewable, keys, selectItem, viewFunction, downloadFunction } = props

  return (
    <tr key={item.id}>
      <StyledTd align="left" key={`${item.id}_checkbox`}>
        <Checkbox
          name="selectItem"
          value="selectItem"
          checked={isSelected}
          onChange={(event) => selectItem(item, event)}
        />
      </StyledTd>
      {keys.map((key) => {
        const ItemKey = key.key
        return (
          <StyledTd align={key.align as any} key={`${item.id}_${item[ItemKey]}`}>
            {key.format ? key.format(item[ItemKey]) : item[ItemKey]}
          </StyledTd>
        )
      })}

      <StyledTd align="center" key={`${item.id}_view`}>
        {viewable && item.documentType === 'pdf' && (
          <ViewButton type="button" onClick={() => viewFunction && viewFunction(item.sharepointId)}>
            <FontAwesomeIcon icon={item.loading ? 'spinner' : 'eye'} spin={item.loading} />
          </ViewButton>
        )}
      </StyledTd>
      <StyledTd align="center" key={`${item.id}_download`}>
        {downloadFunction && item.documentType !== 'Autre' && (
          <ViewButton type="button" onClick={() => downloadFunction(item)}>
            <FontAwesomeIcon icon={item.loading ? 'spinner' : 'download'} spin={item.loading} />
          </ViewButton>
        )}
      </StyledTd>
    </tr>
  )
}

export const getIconSort = (
  currentHeader: string,
  config: SortConfig,
  isMobile?: boolean
): ReactElement => {
  const { direction, key } = config
  let iconUp: IconProp = 'sort-up'
  let iconDown: IconProp = 'sort-down'
  if (isMobile) {
    iconUp = 'arrow-up'
    iconDown = 'arrow-down'
  }
  if (currentHeader === key) {
    if (direction === 'ascending') {
      return <FontAwesomeIcon icon={iconUp} />
    }

    return <FontAwesomeIcon icon={iconDown} />
  }
  return <></>
}

// COMPONENT RENDER
const Table: React.FC<TableProps> = (props) => {
  const {
    headers,
    keys,
    data,
    sortConfig,
    viewable,
    viewFunction,
    downloadFunction,
    downloadMultipleFunction,
    downloadMultipleIsLoading,
    filters,
    handleSelectItem,
    selectedItems,
    handleSelectAll,
    selectAll,
  } = props

  const { t } = useCustomTranslation('common')

  const memoizedFilteredData = useMemo(() => filterData(data, filters), [data, filters])

  const { items: sortedItems, requestSort, sortConfig: currentConfig } = useSortableData(
    memoizedFilteredData,
    sortConfig
  )

  // Création d'un Set pour optimiser la recherche
  const selectedItemsSet = new Set(selectedItems.map((item) => item.sharepointId))

  const getHeader = (): ReactElement[] => {
    return headers.map((header) => {
      return (
        <StyledTh
          align={header.align}
          key={header.value}
          onClick={() => requestSort(header.propertyName, header.sort)}
          className={header.propertyName === currentConfig.key ? 'active' : ''}
        >
          {header.value} {getIconSort(header.propertyName, currentConfig)}
        </StyledTh>
      )
    })
  }

  return (
    <TableWrapper>
      {downloadMultipleIsLoading ? (
        <LoadingDownloadButton>
          <FontAwesomeIcon icon="spinner" spin />
        </LoadingDownloadButton>
      ) : null}
      {downloadMultipleFunction && !downloadMultipleIsLoading && selectedItems.length > 1 ? (
        <DownloadAllButton type="button" onClick={() => downloadMultipleFunction(selectedItems)}>
          {t('download_selection')}
        </DownloadAllButton>
      ) : null}
      <StyledTable>
        <StyledThead>
          <StyledTh>
            <Checkbox
              name="selectAll"
              value="selectAll"
              checked={selectAll}
              onChange={(event) => handleSelectAll(event)}
            />
          </StyledTh>
          {getHeader()}
        </StyledThead>
        <StyledTbody>
          {sortedItems.map((item) => (
            <TableItem
              item={item}
              keys={keys}
              viewable={!!viewable}
              viewFunction={viewFunction}
              downloadFunction={downloadFunction}
              key={item.sharepointId}
              selectItem={handleSelectItem}
              isSelected={selectedItemsSet.has(item.sharepointId)}
            />
          ))}
        </StyledTbody>
      </StyledTable>
    </TableWrapper>
  )
}

export default Table
